import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface SidebarState {
  isOpen: boolean
  isMobileOpen: boolean
  toggleSidebar: () => void
  closeSidebar: () => void
  openSidebar: () => void
  toggleMobileSidebar: () => void
  closeMobileSidebar: () => void
  openMobileSidebar: () => void
}

/**
 * Sidebar State Management with Zustand
 *
 * - `isOpen`: Desktop sidebar collapse/expand state (persisted in localStorage)
 * - `isMobileOpen`: Mobile sidebar drawer state (not persisted)
 * - State persists across navigation within auth routes
 * - Mobile sidebar closes automatically after navigation (handled in component)
 */
export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      isOpen: true, // Desktop sidebar open by default
      isMobileOpen: false, // Mobile sidebar closed by default
      toggleSidebar: () => set((state) => ({ isOpen: !state.isOpen })),
      closeSidebar: () => set({ isOpen: false }),
      openSidebar: () => set({ isOpen: true }),
      toggleMobileSidebar: () => set((state) => ({ isMobileOpen: !state.isMobileOpen })),
      closeMobileSidebar: () => set({ isMobileOpen: false }),
      openMobileSidebar: () => set({ isMobileOpen: true }),
    }),
    {
      name: 'sidebar-state',
      // Only persist desktop sidebar state, not mobile
      partialize: (state) => ({ isOpen: state.isOpen }),
    }
  )
)
